import {
  Box,
  Button,
  createTheme,
  FormControl,
  Typography
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { ThemeProvider } from '@mui/material/styles'
import {
  Grid,
  Stack
} from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useEffect, useState } from "react";
import ColorConstants from "../../../defaults/ColorConstants";
import FieldNames from "../../../defaults/FieldNames.json";
import AuditLogService from "../../../services/AuditLogService";
import CarryoverService from "../../../services/CarryoverService";
import ErrorService from "../../../services/ErrorService";
import FeatureService from "../../../services/FeatureService";
import {
  calculateFiscalYearQuarter,
  getSaveableDate,
  handleUndoCarryoverTargetInitDateChange
} from "../../../utils/DateHelper";
import BasicTextField from "../../details/customPanels/BasicTextField";
import InFormGenericModal from "../../InFormGenericModal";
import { setShowGlobalLoader } from '@store/slices/globalContextSlice';
import { useDispatch } from "react-redux";

export default function UndoCarryoverPopup({
  currentUserInfo,
  userRoleInfo,
  carryoverEditorOpen,
  setCarryoverEditorOpen,
  feature,
  saveCallback
}) {
  const useStyles = makeStyles({
    underline: {
      "&&&:before": {
        borderBottom: "2px solid red",
      },
      "&&:after": {
        borderBottom: "2px solid red",
      },
    },
    condensedFormControl: {
      margin: "5px 15px 5px 15px",
      minWidth: 120,
      width: "90%",
    },
    formControl: {
      margin: "10px 15px 10px 15px",
      minWidth: 120,
      width: "90%",
    },
    formControlHeader: {
      margin: "0px 15px 0px 15px",
      minWidth: 120,
      width: "90%",
    },
    snackBarStyle: {
      top: "120px !important",
    },
  });

  const useErrorStyles = makeStyles({
    underline: {
      "&&&:before": {
        borderBottom: "2px solid red",
      },
      "&&:after": {
        borderBottom: "2px solid red",
      },
    },
  });

  const classes = useStyles();
  const errorClasses = useErrorStyles();

  const theme = createTheme({
    overrides: {
      palette: {
        red: {
          main: "#FF0000",
          light: "#FF0000",
          dark: "#FF0000",
          contrastText: "#242105",
        },
      },
      MuiPaper: {
        root: {
          backgroundColor: "green",
        },
      },
      MuiOutlinedInput: {
        root: {
          "& $notchedOutline": {
            //initial border
            borderColor: ColorConstants.BlueAlien,
          },
          "&:hover $notchedOutline": {
            //border on hover
            borderColor: ColorConstants.BlueAlien,
            border: "2px solid",
          },
          "&$focused $notchedOutline": {
            //border on focus
            borderColor: ColorConstants.BlueAlien,
            border: "2px solid",
          },
        },
      },
    },
  });

  
  const dispatch = useDispatch();

  // State Variables
  const [targetInitiationDateValue, setTargetInitiationDateValue] =
    useState(null);

  const [carryoverReasoning, setCarryoverReasoning] = useState("");
  const [
    carryoverTargetFiscalYearSelection,
    setCarryoverTargetFiscalYearSelection,
  ] = useState(null);
  const [currentError, setCurrentError] = useState("");
  const [toShowError, setToShowError] = useState(false);

  // Logic

  useEffect(() => {
    setCarryoverReasoning("");
  }, [carryoverEditorOpen]);

  const closeCarryoverEditor = () => {
    setCarryoverEditorOpen(false);
    setCarryoverReasoning("");
    setTargetInitiationDateValue(null);
  };

  const undoLastCarryover = async () => {
    try
    {
      dispatch(setShowGlobalLoader(true));
      let targetFySelection = Number(
        carryoverTargetFiscalYearSelection[FieldNames.carryoverFields.fiscalYear]
      );

      const allCarryoverRows = await CarryoverService.getAllOrderedCarryoverForFeature(feature?.[FieldNames.featureFields.joinID], feature?.[FieldNames.featureFields.joinFeature])
      // Check if feature was marked as carryover multiple times
      const willFeatureStillBeCarryover = allCarryoverRows?.length > 1;
      const newestCarryoverEntry = allCarryoverRows[0];

      let carryoverObjToArchive = {
        OBJECTID: newestCarryoverEntry?.OBJECTID,
        IsArchived: FieldNames.yesNo.yes
      }

      let featureObj = {
        OBJECTID: feature[FieldNames.featureFields.entityID],
        IsCarryover: willFeatureStillBeCarryover ? FieldNames.yesNo.yes : FieldNames.yesNo.no,
        CarryoverFiscalYear: willFeatureStillBeCarryover ? calculateFiscalYearQuarter(getSaveableDate(new Date()), true) : null,
        InitiationDate: getSaveableDate(targetInitiationDateValue),
        InitiationFiscalYear: targetFySelection,
        InitiationFiscalQuarter: calculateFiscalYearQuarter(
          new Date(targetInitiationDateValue),
          false
        ),
      };

      let baseMessage = `Undoing previous Carryover for this Action`;
      let auditLogMessage = carryoverReasoning
        ? baseMessage + ". " + carryoverReasoning
        : baseMessage;
      const featureResp = await FeatureService.updateFeatureAttributes([featureObj]);
      const carryoverResp = await CarryoverService.updateCarryoverEntry(carryoverObjToArchive);
      const auditResp = await AuditLogService.addAuditLogEntry(
                feature,
                feature[FieldNames.featureFields.joinID],
                feature[FieldNames.featureFields.joinFeature],
                currentUserInfo[FieldNames.userFields.nifcUsername],
                auditLogMessage
              );
      if(saveCallback)
      {
        saveCallback();
      }
      setCarryoverReasoning("");
      setTargetInitiationDateValue(null);
    }
    catch(e)
    {
      ErrorService.addErrorLogEntry(
        userRoleInfo[FieldNames.userFields.nifcUsername],
        e
      );
    }
    finally
    {
      dispatch(setShowGlobalLoader(false));
    }
  };

  return (
    <InFormGenericModal
      openFlag={carryoverEditorOpen}
      closeMethod={closeCarryoverEditor}
      titleText={"Undo Carryover"}
      displayButton={false}
    >
      <Box
        style={{
          maxHeight: "300px",
          overflow: "auto",
          width: "100%",
          minWidth: "450px",
        }}
      >
        <FormControl variant="standard" className={classes.formControl}>
          <ThemeProvider theme={theme}>
            <Grid
              item
              size={12}
              container
              direction="row"
              className="grid-item"
              style={{ alignItems: "center" }}
            >
            <Grid
              item
              size={12}
              container
              direction="row"
              className="grid-item"
              style={{ alignItems: "center" }}
            >
              <Grid item size={12}>
                <BasicTextField
                  field="carryoverReason"
                  label=""
                  setValue={setCarryoverReasoning}
                  variant={"outlined"}
                  placeholder={"Reason"}
                  useFullWidth={true}
                />
              </Grid>
            </Grid>
              <Grid item size={12}>
                <Typography display={"inline"}>
                  Target Initiation Date:{" "}
                </Typography>
                <Typography display={"inline"} color={"red"}>
                  {" "}
                  *
                </Typography>
              </Grid>
              <Grid item size={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    format="MM/dd/yyyy"
                    fullWidth
                    clearable
                    clearText="Clear"
                    {...(!targetInitiationDateValue
                      ? { InputProps: { errorClasses } }
                      : { InputProps: {} })}
                    id={`datePicker-date`}
                    value={targetInitiationDateValue}
                    onChange={(value) =>
                      handleUndoCarryoverTargetInitDateChange(
                        value,
                        setTargetInitiationDateValue,
                        setCurrentError,
                        setToShowError,
                        setCarryoverTargetFiscalYearSelection
                      )
                    }
                    slotProps={{
                      textField: {
                        helperText: toShowError ? currentError ?? "" : "",
                        error: toShowError
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </ThemeProvider>
        </FormControl>
        <Stack direction={"row"} sx={{marginLeft: `75%`, marginRight: '5px', marginBottom: '5px'}}>
          <Button
            variant="outlined"
            onClick={() => {
              setCarryoverReasoning("");
              setTargetInitiationDateValue(null);
              setCarryoverEditorOpen(false);
            }}
            aria-label="cancel"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              undoLastCarryover();
              setCarryoverEditorOpen(false);
            }}
            disabled={
              !carryoverTargetFiscalYearSelection
            }
            aria-label="edit"
            sx={{ marginLeft: "5px" }}
          >
            Save
          </Button>
        </Stack>
      </Box>
    </InFormGenericModal>
  );
}
